import { useEffect, useState } from "react";
import api from "../lib/api";
import { Calendar, Clock, MapPin, X, Receipt, FileText, Mail, MessageCircle } from "lucide-react";
import { ddmmyyyy, time12h, weekdayLong } from "../lib/dates";
import { toast, Toaster } from "sonner";
import { downloadFile } from "../lib/download";

export default function MyBookings() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("upcoming");

    const fetch = async () => {
        const { data } = await api.get("/bookings/me");
        setBookings(data);
        setLoading(false);
    };

    useEffect(() => { fetch(); }, []);

    const today = new Date().toISOString().split("T")[0];
    const upcoming = bookings.filter(b => b.booking_date >= today && b.status !== "cancelled");
    const past = bookings.filter(b => b.booking_date < today || b.status === "cancelled");
    const list = tab === "upcoming" ? upcoming : past;

    const cancel = async (id) => {
        if (!confirm("Cancel this booking?\n\nRefund policy:\n• More than 24h before slot: 100% auto-refund (instant)\n• 24h–3h before: 50% refund — admin approval required\n• Within 3h or no-show: no refund (still goes through admin)\n\nApproved refunds reach your original payment source in 3–7 business days.")) return;
        try {
            const { data } = await api.post(`/bookings/${id}/cancel`);
            if (data.auto_refund) {
                toast.success(`Cancelled. ₹${data.refund} refund initiated (${data.tier_pct}%).`);
            } else if (data.tier_pct === 0) {
                toast.success("Cancellation request submitted — no refund applicable (within 3h). Awaiting admin confirmation.");
            } else {
                toast.success(`Cancellation request submitted. Suggested refund: ₹${data.suggested_refund} (${data.tier_pct}%). Awaiting admin approval.`);
            }
            fetch();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cancel failed");
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
            <Toaster theme="dark" richColors position="top-center" />
            <h1 className="font-display text-3xl font-bold uppercase text-white mb-2">My Bookings</h1>
            <p className="text-slate-400 text-sm mb-6">All your turf reservations in one place.</p>

            <div className="flex gap-2 mb-6">
                {["upcoming", "past"].map(t => (
                    <button key={t} onClick={() => setTab(t)}
                        data-testid={`tab-${t}`}
                        className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-widest border ${
                            tab === t ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800 hover:border-[#00FF66]/50"
                        }`}>{t} ({t === "upcoming" ? upcoming.length : past.length})</button>
                ))}
            </div>

            {loading ? <div className="text-slate-500 text-center py-12">Loading...</div> :
                list.length === 0 ? <div className="text-slate-500 text-center py-12">No {tab} bookings yet.</div> :
                <div className="space-y-3">
                    {list.map(b => (
                        <div key={b.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-5" data-testid={`booking-card-${b.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-display font-bold text-lg text-white uppercase">{b.sport}</span>
                                        <StatusBadge status={b.status} />
                                    </div>
                                    <div className="text-sm text-slate-300">{b.centre_name} · {b.turf_name}</div>
                                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /><span className="font-bold text-white">{weekdayLong(b.booking_date)}</span>,&nbsp;{ddmmyyyy(b.booking_date)}</span>
                                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{time12h(b.start_time)} – {time12h(b.end_time)}</span>
                                        <span className="inline-flex items-center gap-1"><Receipt className="w-3 h-3" />{b.booking_code}</span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-slate-400 uppercase tracking-widest">Paid</div>
                                    <div className="font-display text-2xl font-bold text-[#00FF66]">₹{b.paid_amount}</div>
                                    {b.balance_due > 0 && <div className="text-xs text-amber-400 mt-0.5">+₹{b.balance_due} at venue</div>}
                                    {b.status === "cancelled" && b.refund_amount != null && <div className="text-xs text-slate-400 mt-0.5">Refund: ₹{b.refund_amount}</div>}
                                    {b.cancellation_status === "requested" && <div className="text-[10px] uppercase tracking-widest text-amber-400 mt-0.5 font-bold">Awaiting admin approval</div>}
                                    {b.cancellation_status === "declined" && <div className="text-[10px] uppercase tracking-widest text-red-400 mt-0.5 font-bold">Cancellation declined</div>}
                                </div>
                            </div>
                            {(tab === "upcoming" || tab === "past") && b.status !== "cancelled" && (
                                <div className="mt-4 pt-3 border-t border-slate-800 flex flex-wrap justify-end gap-2">
                                    <button onClick={async () => {
                                        try { await downloadFile(`/bookings/${b.id}/invoice.pdf`, `invoice_${b.booking_code}.pdf`); }
                                        catch (e) { toast.error("Could not download invoice"); }
                                    }} data-testid={`invoice-dl-${b.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-8 text-xs font-bold uppercase tracking-widest rounded border border-slate-700 text-slate-200 hover:border-[#00FF66]/50">
                                        <FileText className="w-3 h-3" /> Invoice
                                    </button>
                                    <button onClick={async () => {
                                        try {
                                            const { data } = await api.post(`/bookings/${b.id}/send-invoice`, null, { params: { channel: "email" } });
                                            toast.success(`Emailed to ${data.to}`);
                                        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                                    }} data-testid={`invoice-email-${b.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-8 text-xs font-bold uppercase tracking-widest rounded border border-slate-700 text-slate-200 hover:border-[#00FF66]/50">
                                        <Mail className="w-3 h-3" /> Email
                                    </button>
                                    <button onClick={async () => {
                                        try {
                                            const { data } = await api.post(`/bookings/${b.id}/send-invoice`, null, { params: { channel: "whatsapp" } });
                                            toast.success(`Sent to ${data.to}`);
                                        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                                    }} data-testid={`invoice-whatsapp-${b.id}`}
                                        className="inline-flex items-center gap-1 px-3 h-8 text-xs font-bold uppercase tracking-widest rounded border border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10">
                                        <MessageCircle className="w-3 h-3" /> WhatsApp
                                    </button>
                                    {tab === "upcoming" && b.cancellation_status !== "requested" && (
                                        <button onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}
                                            className="inline-flex items-center gap-1 px-3 h-8 text-xs font-bold uppercase tracking-widest rounded border border-red-900 text-red-400 hover:bg-red-950 transition">
                                            <X className="w-3 h-3" /> Cancel
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            }
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        confirmed: "bg-[#00FF66]/15 text-[#00FF66] border-[#00FF66]/40",
        cancelled: "bg-red-950 text-red-400 border-red-900",
        completed: "bg-slate-800 text-slate-400 border-slate-700",
    };
    return <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border ${map[status] || map.completed}`}>{status}</span>;
}
